import { createSlice } from '@reduxjs/toolkit';
import {
  request,
  generateCancelToken,
  cancelRequests,
  isCancel,
  ejectCancelInterceptor,
} from 'modules/Api/HttpClient';
import {
  SCHOOLS_URL,
  SCHOOL_BY_ID_URL,
  SCHOOLS_CREATE_URL,
  SCHOOLS_UPDATE_URL,
  SCHOOLS_DELETE_URL,
  USER_SCHOOL_ADMINS_BY_SCHOOL_URL,
  STUDENTS_BY_SCHOOL_URL,
  SCHOOLS_BY_UNIT_URL,
} from 'modules/Api/Routes';
import { formDataFromObj } from 'modules/Api/RequestData';
import { createSchoolRequestModel } from 'modules/Institutions/Schools/SchoolUtils';
import { defaultRequest } from 'modules/Utils/DefaultRequest';

let cancelToken;

const initialState = {
  loading: false,
  error: null,
  data: {
    page: 1,
    perPage: 10,
    search: '',
    groupId: null,
    unitId: null,
    sort: {
      sortType: null,
      sortBy: null,
    },
  },
  schoolStudents: {
    schoolId: '',
    name: '',
    students: [],
    total: 0,
    page: 1,
    perPage: 8,
    search: '',
    showInfo: false,
    align: 'center',
    loading: false,
  },
  schoolAdmins: {
    schoolId: '',
    name: '',
    admins: [],
    total: 0,
    page: 1,
    perPage: 8,
    search: '',
    showInfo: false,
    align: 'center',
    loading: false,
  },
  isDeleting: false,
  schoolsSelect: {
    data: [],
    loading: false,
    error: null,
  },
};

const schoolsSlice = createSlice({
  name: 'school',
  initialState,
  reducers: {
    cancelRequests: () => {
      cancelToken?.cancel();
      cancelRequests();
    },
    cleanState: () => ({ ...initialState }),
    /**
     * indicate that a request is started
     */
    changeSort: (state, action) => {
      const newSort = action.payload;
      state.data.sort.sortType =
        state.data.sort.sortBy === newSort && state.data.sort.sortType === 'ASC'
          ? 'DESC'
          : 'ASC';
      state.data.sort.sortBy = action.payload;
    },
    changePerPage: (state, action) => {
      state.data.perPage = action.payload;
      state.data.page = 1;
    },
    requestSchool: (state) => {
      state.loading = true;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setIsDeleting: (state, action) => {
      state.isDeleting = action.payload;
    },
    /**
     * receive a success response
     */
    receiveRequestSuccess: (state) => {
      state.loading = false;
    },
    /**
     * receive a success school list response
     */
    receiveSchoolList: (state, action) => {
      state.loading = false;
      state.data = {
        ...state.data,
        schools: action.payload.schools,
        total: action.payload.total_items,
      };
    },
    clearSchoolsList: (state) => {
      state.loading = false;
      state.data = {
        page: 1,
        perPage: 10,
        search: '',
        groupId: null,
        unitId: null,
        sort: {
          sortType: null,
          sortBy: null,
        },
      };
      state.schoolStudents = {
        schoolId: '',
        name: '',
        students: [],
        total: 0,
        page: 1,
        perPage: 8,
        search: '',
        showInfo: false,
        align: 'center',
        loading: false,
      };
      state.schoolAdmins = {
        schoolId: '',
        name: '',
        admins: [],
        total: 0,
        page: 1,
        perPage: 8,
        search: '',
        showInfo: false,
        align: 'center',
        loading: false,
      };
    },
    /**
     * receive an error response
     */
    receiveSchoolError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    changeSchoolsPage: (state, action) => {
      state.data.page = action.payload;
    },
    changeSchoolsSearch: (state, action) => {
      state.data.groupId = action.payload.group_id;
      state.data.unitId = action.payload.unit_id;
      state.data.search = action.payload.search;
      state.data.page = 1;
    },
    changeSchoolFilters: (state, action) => {
      state.data.groupId = action.payload?.group_id || '';
      state.data.unitId = action.payload?.unit_id || '';
      state.data.page = isNaN(action?.payload?.page)
        ? 1
        : Number(action?.payload?.page);
      state.data.perPage = isNaN(action?.payload?.paginates_per)
        ? state.data.perPage
        : Number(action?.payload?.paginates_per);
      state.data.search = action?.payload.search || '';
    },
    updateSchoolOnList: (state, action) => {
      const updSchool = action.payload;
      const index = state.data.schools.findIndex(
        (school) => school.id === updSchool.id
      );
      if (index !== -1) state.data.schools.splice(index, 1, updSchool);
    },
    requestStudents: (state) => {
      state.schoolStudents.loading = true;
      state.schoolStudents.error = null;
    },
    receiveStudentsList: (state, action) => {
      state.schoolStudents = {
        ...state.schoolStudents,
        schoolId: action.payload.id || state.schoolStudents.unitId,
        name: action.payload.name || state.schoolStudents.name,
        students: action.payload.students,
        total: action.payload.total_items,
        loading: false,
      };
    },
    receiveStudentsError: (state, action) => {
      state.schoolStudents.loading = false;
      state.schoolStudents.error = action.payload;
    },
    requestAdmins: (state) => {
      state.schoolAdmins.loading = true;
      state.schoolAdmins.error = null;
    },
    receiveAdminsList: (state, action) => {
      state.schoolAdmins = {
        ...state.schoolAdmins,
        schoolId: action.payload.id || state.schoolAdmins.unitId,
        name: action.payload.name || state.schoolAdmins.name,
        admins: action.payload.users,
        total: action.payload.total_items,
        loading: false,
      };
    },
    receiveAdminsError: (state, action) => {
      state.schoolAdmins.loading = false;
      state.schoolAdmins.error = action.payload;
    },
    changeStudentsFormSearch: (state, action) => {
      state.schoolStudents.search = action.payload.search;
      state.schoolStudents.page = 1;
    },
    changeStudentsFormPage: (state, action) => {
      state.schoolStudents.page = action.payload;
    },
    changeAdminsFormSearch: (state, action) => {
      state.schoolAdmins.search = action.payload.search;
      state.schoolAdmins.page = 1;
    },
    changeAdminsFormPage: (state, action) => {
      state.schoolAdmins.page = action.payload;
    },
    clearStudents: (state) => {
      state.schoolStudents.search = '';
      state.schoolStudents.page = 1;
    },
    clearAdmins: (state) => {
      state.schoolAdmins.search = '';
      state.schoolAdmins.page = 1;
    },
    requestSchoolsSelect: (state) => {
      state.schoolsSelect.loading = true;
      state.schoolsSelect.error = null;
    },
    receiveSchoolsSelectList: (state, action) => {
      state.schoolsSelect.data = action.payload?.content?.schools;
      state.schoolsSelect.loading = false;
    },
    receiveSchoolsSelectError: (state, action) => {
      state.schoolsSelect.loading = false;
      state.schoolsSelect.error = action.payload;
    },
  },
});

const Actions = schoolsSlice.actions;

const Selectors = {
  fetchListData: (state) => state.school,
  schoolLoading: ({ school: { loading } }) => ({ loading }),
  fetchSchoolsSelect: (state) => state.school.schoolsSelect,
  schoolsSelectLoading: ({
    school: {
      schoolsSelect: { loading },
    },
  }) => ({
    loading,
  }),
};

const Async = {
  fetchSchoolsList: () => async (dispatch, getState) => {
    const {
      school: {
        data: {
          page,
          perPage,
          search,
          groupId,
          unitId,
          sort: { sortType, sortBy },
        },
      },
    } = getState();

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    let action;

    dispatch(Actions.requestSchool());

    try {
      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: SCHOOLS_URL,
        params: {
          page,
          paginates_per: perPage,
          search,
          group_id: groupId,
          unit_id: unitId,
          sort: sortType,
          sort_by: sortBy,
        },
      });

      action = Actions.receiveSchoolList(response.data.content);
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveSchoolError(e.message);
      }
    }

    action && dispatch(action);
  },

  fetchSchoolStudents:
    ({ id, name } = {}) =>
    async (dispatch, getState) => {
      const {
        school: {
          schoolStudents: { page, perPage, search, schoolId, name: schoolName },
        },
      } = getState();

      let action;

      dispatch(Actions.requestStudents());

      try {
        const response = await request({
          method: 'GET',
          url: STUDENTS_BY_SCHOOL_URL,
          params: {
            page,
            paginates_per: perPage,
            school_id: id || schoolId,
            search,
          },
        });

        const data = {
          ...response.data.content,
          id: id || schoolId,
          name: name || schoolName,
        };

        action = Actions.receiveStudentsList(data);
      } catch (e) {
        action = Actions.receiveStudentsError(e.message);
      }

      dispatch(action);
    },

  fetchSchoolAdmins:
    ({ id, name } = {}) =>
    async (dispatch, getState) => {
      const {
        school: {
          schoolAdmins: { page, perPage, search, schoolId, name: schoolName },
        },
      } = getState();

      let action;

      dispatch(Actions.requestAdmins());

      try {
        const response = await request({
          method: 'GET',
          url: USER_SCHOOL_ADMINS_BY_SCHOOL_URL,
          params: {
            page,
            paginates_per: perPage,
            school_id: id || schoolId,
            search,
          },
        });

        const data = {
          ...response.data.content,
          id: id || schoolId,
          name: name || schoolName,
        };

        action = Actions.receiveAdminsList(data);
      } catch (e) {
        action = Actions.receiveAdminsError(e.message);
      }

      dispatch(action);
    },

  getSchoolById:
    ({ id, onSuccess, onError }) =>
    async () => {
      try {
        const response = await request({
          method: 'GET',
          url: `${SCHOOL_BY_ID_URL}?id=${id}`,
        });

        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  createSchool:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestSchool());
      try {
        const parsedSchool = createSchoolRequestModel(data);
        delete parsedSchool.id;

        const schoolData = formDataFromObj(parsedSchool);

        const response = await request({
          method: 'POST',
          url: SCHOOLS_CREATE_URL,
          data: schoolData,
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveSchoolError());
        onError(e);
      }
    },

  updateSchool:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestSchool());
      try {
        const parsedSchool = createSchoolRequestModel(data);

        const schoolData = formDataFromObj(parsedSchool);

        const response = await request({
          method: 'PUT',
          url: SCHOOLS_UPDATE_URL,
          data: schoolData,
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveSchoolError());
        onError(e);
      }
    },

  deleteSchool:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.setIsDeleting(true));

      try {
        const response = await request({
          method: 'DELETE',
          url: `${SCHOOLS_DELETE_URL}?id=${id}`,
        });

        onSuccess(response);
        dispatch(Actions.setIsDeleting(false));
      } catch (e) {
        onError(e);
        dispatch(Actions.setIsDeleting(false));
      }
    },

  fetchSchoolsListByUnitId:
    ({ unitId }) =>
    async (dispatch) =>
      defaultRequest(
        dispatch,
        `${SCHOOLS_BY_UNIT_URL}?unit_id=${unitId}`,
        'GET',
        Actions.requestSchoolsSelect,
        Actions.receiveSchoolsSelectList,
        Actions.receiveSchoolsSelectError
      ),
};

const reducer = schoolsSlice.reducer;

export { reducer, Actions, Async, Selectors };
